import type { VeivoValidationFlowHandle, VeivoValidationFlowOptions } from "@veivo/web-sdk";
import { RefObject, useEffect, useRef } from "react";
import { veivoHandoffUrl } from "@/helpers/veivoHandoffUrl";
import { veivoThemeFromBranding } from "@/helpers/veivoTheme";
import { useOperator } from "@/hooks";

/** Where the SDK reaches Veivo and with what: minted by v2 for this page. */
export interface VeivoSession {
  validationId: string;
  sessionToken: string;
  veivoBaseUrl: string;
}

/** The page draws the step's card; the panel sits in it without one of its own. */
const PANEL_CSS = ".flow { border: 0; padding: 0; max-width: none; }";

type Mount = (host: HTMLElement, options: Omit<VeivoValidationFlowOptions, "mode">) => VeivoValidationFlowHandle;
type Callbacks = Pick<
  VeivoValidationFlowOptions,
  "onDocumentsValid" | "onProgress" | "onComplete" | "onUseThisDevice" | "onClose" | "onHelp"
>;

/**
 * Mount one of the SDK's step panels into `host` (DEV-1902), in the operator's
 * colours, with the hand-off going to the public verification page.
 *
 * Callbacks are read through a ref: the SDK keeps the ones it was mounted with,
 * and a new function per render must not remount the panel — that would close
 * the camera, or lose an upload in flight. It remounts only for a new session.
 */
export function useVeivoPanel(
  host: RefObject<HTMLDivElement>,
  mount: Mount,
  session: VeivoSession | null,
  callbacks: Callbacks,
  extra: Pick<VeivoValidationFlowOptions, "fullscreen"> & {
    /** A change remounts the panel: one that showed "rejected" stops
     *  polling, and must start over when staff let it continue (DEV-2002). */
    remountKey?: string;
  } = {}
): void {
  const { branding } = useOperator();
  // The colour, not the branding object: a new object per render must not
  // remount the panel.
  const primary = branding?.primary;
  const latest = useRef(callbacks);
  latest.current = callbacks;
  const fullscreen = Boolean(extra.fullscreen);

  useEffect(() => {
    const element = host.current;
    if (!element || !session) return;
    const handle = mount(element, {
      service: { url: session.veivoBaseUrl },
      resumeValidationId: session.validationId,
      sessionToken: session.sessionToken,
      theme: veivoThemeFromBranding({ primary }),
      customCss: PANEL_CSS,
      handoffUrl: (params) => veivoHandoffUrl(params, primary),
      // Build-time testing affordance; the service must also allow it.
      allowTestSkip: import.meta.env.VITE_VEIVO_ALLOW_TEST_SKIP === "true",
      fullscreen,
      onError: (error) => console.error("Veivo panel error:", error),
      onDocumentsValid: (validation) => latest.current.onDocumentsValid?.(validation),
      onProgress: (progress) => latest.current.onProgress?.(progress),
      onComplete: (validation) => latest.current.onComplete?.(validation),
      ...(callbacks.onUseThisDevice ? { onUseThisDevice: () => latest.current.onUseThisDevice?.() } : {}),
      ...(callbacks.onClose ? { onClose: () => latest.current.onClose?.() } : {}),
      onHelp: (topic) => latest.current.onHelp?.(topic),
    });
    return () => {
      void handle.dispose();
    };
    // `callbacks` is read through the ref; only whether each optional one
    // exists matters at mount, and that does not change for a step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, mount, session?.validationId, session?.sessionToken, session?.veivoBaseUrl, primary, fullscreen, extra.remountKey]);
}
